import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Autocomplete,
  FormControlLabel,
  Grid2,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid-pro";
import isEqual from "lodash/isEqual";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { format } from "date-fns";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import useApi from "../../../hooks/useApi";
import useParameterLessApi from "../../../hooks/useParemeterLessApi";
import {
  dataGridHeaderHeight,
  dataGridRowHeight,
  GLOBALSETTINGS,
  GRIDCOLUMNKEYS,
  ModuleNames,
  SEARCHBOX,
  SEVERITIES,
} from "../../../data/constants";
import { EDITRESOURCEMESSAGES } from "../../../constants/messages";
import { filterDataBySearch } from "../../../utils/commonHelper";
import SearchBox from "../../../components/form/SearchBox/SearchBox";
import ConfirmationDialog from "../../../components/ui/ConfirmationDialog";
import { useToaster } from "../../../hooks/useToaster";
import CustomLoadingOverlay from "../../../components/ui/CustomLoadingOverlay";
import { useResizableColumns } from "../../../hooks/useResizableColumns";
import { usePersistedColumnVisibility } from "../../../hooks/usePersistedColumnVisibility";
import { useMenu } from "../../../context/MenuContext";
import { useUser } from "../../../context/UserContext";
import { IpWhiteListDto } from "../../../types/IpWhitelist";
import {
  getIpWhiteList,
  addIpWhiteList,
  updateIpWhiteList,
  deleteIpWhiteList,
  searchWhitelistUsers,
} from "../../../services/ipWhitelistService";
import { getPortalListByCategoryDescription } from "../../../services/portalListService";
import { PortalListDto, PortalListCategoryDto } from "../../../types/PortalList";
import { UserDto } from "../../../types/User";
import { logAuditTrace } from "../../../services/auditNavigationTraceService";
import { getCurrentESTTime } from "../../../utils/dateHelper";

// List category (Portal_ListCategories.Description) that holds the Blacklist Type values.
const BLACKLIST_TYPE_CATEGORY = "IPAddressBlacklisttype";
const BLACKLIST_TYPE_DEFAULT = "Admin";
// IP Color dropdown: stored value (W/B) -> label shown to the user.
const IP_COLOR_OPTIONS = [
  { value: "W", label: "Whitelisted/allow" },
  { value: "B", label: "Blacklisted/block" },
];
const IP_COLOR_DEFAULT = "W";

// User dropdown display fallback: username, else cached email, else cached phone.
const userDisplay = (u: UserDto) =>
  u.userName?.trim() || u.cachedEmailAddress?.trim() || u.cachedPhoneNumber?.trim() || "";

// Accept only a valid IPv4 address (IPv6 is not supported).
const IPV4_REGEX = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/;
const isValidIp = (ip: string) => IPV4_REGEX.test(ip.trim());

// Special-purpose IPv4 addresses that are never a real client's source IP, so they can never
// match an incoming request and must not be whitelisted/blacklisted:
//  - Unspecified / wildcard (0.0.0.0)
//  - Loopback (127.0.0.0/8)
//  - Broadcast (255.255.255.255)
//  - Multicast (224.0.0.0/4)
const isNonClientIp = (ip: string): boolean => {
  // Already validated as a well-formed IPv4 by isValidIp before this runs.
  const octets = ip.trim().split(".").map((o) => Number(o));
  if (octets.length !== 4) return false;
  const [a, b, c, d] = octets;
  if (a === 0 && b === 0 && c === 0 && d === 0) return true; // unspecified 0.0.0.0
  if (a === 127) return true; // loopback 127.0.0.0/8
  if (a === 255 && b === 255 && c === 255 && d === 255) return true; // broadcast
  if (a >= 224 && a <= 239) return true; // multicast 224.0.0.0/4
  return false;
};

// Today (local) as YYYY-MM-DD, for date-input min constraints.
const todayStr = () => new Date().toLocaleDateString("en-CA");

// Parse a "YYYY-MM-DD" string as a local-time Date (the plain `new Date(str)` constructor
// treats bare ISO date strings as UTC midnight, which shifts the displayed day backward
// by one in negative-UTC-offset timezones such as this app's EST).
const parseLocalDate = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const emptyForm: IpWhiteListDto = {
  whitelistID: 0,
  ipAddress: "",
  isHamaspikNetwork: false,
  userId: null,
  validFrom: null,
  validUntil: null,
  memo: null,
  blacklistType: null,
  ipColor: IP_COLOR_DEFAULT,
};

function WhiteListedIpList() {
  const { parameterlessapiloading: listLoading, parameterlessexecuteApi: fetchList } = useParameterLessApi();
  const { loading: mutateLoading, executeApi } = useApi();
  // Separate API instance for the type-ahead user search so its per-keystroke loading
  // does NOT toggle the shared `loading` that disables the Add/Update button (avoids button flicker).
  // Separate hook so type-ahead user search does not flip mutateLoading (which would
  // flicker-disable the Add/Update button and the grid overlay on every keystroke).
  const { executeApi: searchUsersApi } = useApi();
  const loading = listLoading || mutateLoading;

  const [allData, setAllData] = useState<IpWhiteListDto[]>([]);
  const [searchText, setSearchText] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [form, setForm] = useState<IpWhiteListDto>(emptyForm);
  const [originalForm, setOriginalForm] = useState<IpWhiteListDto | null>(null);
  const [ipAddressError, setIpAddressError] = useState("");
  const [validUntilError, setValidUntilError] = useState("");
  const [duplicateError, setDuplicateError] = useState("");
  const [entryToDelete, setEntryToDelete] = useState<IpWhiteListDto | null>(null);
  const [blacklistTypes, setBlacklistTypes] = useState<PortalListDto[]>([]);
  const [users, setUsers] = useState<UserDto[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserDto | null>(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userInput, setUserInput] = useState("");

  const showToaster = useToaster();
  const { permissions } = useMenu();
  const { userId } = useUser();

  const ipWhiteListPermission = permissions?.find(
    (p) => p.moduleName === ModuleNames.IpWhiteList,
  );
  const canEdit = ipWhiteListPermission?.canEdit ?? false;
  const canDelete = ipWhiteListPermission?.canDelete ?? false;

  const loadData = useCallback(async () => {
    const data = await fetchList<IpWhiteListDto[]>(getIpWhiteList);
    setAllData(data ?? []);
  }, [fetchList]);

  const loadBlacklistTypes = useCallback(async () => {
    const category = await executeApi<string, PortalListCategoryDto>(
      getPortalListByCategoryDescription,
      BLACKLIST_TYPE_CATEGORY,
    );
    setBlacklistTypes(category?.portalLists ?? []);
  }, [executeApi]);

  // Server-side type-ahead user search. The effect cleanup marks the previous request inactive and
  // clears its pending timer, so a slower/broader earlier query can never overwrite the latest input.
  useEffect(() => {
    if (!isFormOpen) return;
    const term = userInput.trim();
    if (!term) {
      setUsers([]);
      return;
    }
    let active = true;
    setUsersLoading(true);
    const handle = setTimeout(async () => {
      const data = await searchUsersApi<string, UserDto[]>(searchWhitelistUsers, term);
      if (active) {
        setUsers(data ?? []);
        setUsersLoading(false);
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(handle);
    };
  }, [userInput, isFormOpen, searchUsersApi]);

  useEffect(() => {
    loadData();
    loadBlacklistTypes();
  }, [loadData, loadBlacklistTypes]);

  const defaultBlacklistTypeId = () =>
    blacklistTypes.find(
      (t) => t.listDescription?.toLowerCase() === BLACKLIST_TYPE_DEFAULT.toLowerCase(),
    )?.listId ?? null;

  // Record a user activity in Portal_AuditNavigationTraces.
  const traceActivity = useCallback(
    (action: string, objectId?: number | null, objectName?: string | null) => {
      logAuditTrace(
        {
          userId,
          objectType: "IPWhitelist",
          objectId: objectId ?? null,
          objectName: objectName ?? "",
          action,
          navigationDateTime: getCurrentESTTime(),
        },
        null,
        true,
      );
    },
    [userId],
  );

  useEffect(() => {
    if (userId) traceActivity("Navigate to Whitelisted IP List");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const baseData = searchText
    ? filterDataBySearch(allData, searchText.toLowerCase()) ?? []
    : allData;
  // Resolve the Blacklist Type description into the row itself. Doing this at row level (rather than in a
  // column valueFormatter) is required because useResizableColumns snapshots the columns once, which would
  // otherwise freeze the closure over an empty blacklistTypes list before it finishes loading.
  const filteredData = baseData.map((row) => ({
    ...row,
    blacklistTypeName:
      blacklistTypes.find((t) => t.listId === row.blacklistType)?.listDescription ?? "",
  }));

  const handleSearch = useCallback(
    (text: string) => {
      setSearchText(text);
      if (text?.trim()) traceActivity("Searched on Whitelisted IP List", null, text);
    },
    [traceActivity],
  );

  const openAddDialog = () => {
    setForm({ ...emptyForm, blacklistType: defaultBlacklistTypeId(), ipColor: IP_COLOR_DEFAULT });
    setSelectedUser(null);
    setUsers([]);
    setUserInput("");
    setIpAddressError("");
    setValidUntilError("");
    setDuplicateError("");
    setIsEditMode(false);
    setIsFormOpen(true);
    traceActivity("Opened Add popup on Whitelisted IP List");
  };

  const openEditDialog = (row: IpWhiteListDto) => {
    setForm({ ...row });
    setOriginalForm({ ...row });
    // Seed the selected user so the dropdown shows the current value without a search.
    const seededUser = row.userId
      ? { userId: row.userId, userName: row.userName, cachedEmailAddress: null, cachedPhoneNumber: null }
      : null;
    setSelectedUser(seededUser);
    setUserInput(seededUser ? userDisplay(seededUser) : "");
    setUsers([]);
    setIpAddressError("");
    setValidUntilError("");
    setDuplicateError("");
    setIsEditMode(true);
    setIsFormOpen(true);
    traceActivity("Opened Edit popup on Whitelisted IP List", row.whitelistID, row.ipAddress);
  };

  const openDeleteDialog = (row: IpWhiteListDto) => {
    setEntryToDelete(row);
    setIsDeleteOpen(true);
  };

  const handleFormChange = (field: keyof IpWhiteListDto, value: IpWhiteListDto[keyof IpWhiteListDto]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === "ipAddress") setIpAddressError("");
    if (field === "validUntil" || field === "validFrom") setValidUntilError("");
    // Any change to the identifying fields clears a prior duplicate warning so the user can retry.
    if (field === "ipAddress" || field === "userId" || field === "ipColor") setDuplicateError("");
  };

  const handleSave = async () => {
    // Trim leading/trailing spaces so the stored value is normalized (and dedup compares correctly).
    const trimmedIp = (form.ipAddress ?? "").trim();
    if (!trimmedIp) {
      setIpAddressError("IP address is required.");
      return;
    }
    if (!isValidIp(trimmedIp)) {
      setIpAddressError("Enter a valid IPv4 address.");
      return;
    }
    if (isNonClientIp(trimmedIp)) {
      setIpAddressError("This is a special-purpose address (unspecified, loopback, broadcast, or multicast) and cannot be used. Enter a usable client IP address.");
      return;
    }
    if (trimmedIp !== form.ipAddress) {
      setForm((prev) => ({ ...prev, ipAddress: trimmedIp }));
    }
    const payload: IpWhiteListDto = { ...form, ipAddress: trimmedIp };
    const validFrom = form.validFrom ? form.validFrom.substring(0, 10) : "";
    const validUntil = form.validUntil ? form.validUntil.substring(0, 10) : "";
    if (validUntil) {
      if (validUntil < todayStr()) {
        setValidUntilError("Valid Until must be today or a future date.");
        return;
      }
      if (validFrom && validUntil < validFrom) {
        setValidUntilError("Valid Until cannot be earlier than Valid From.");
        return;
      }
    }
    // The _I/_U stored procedures return -1 when an active record with the same
    // IP Address + User + IP Color already exists (duplicate). Keep the dialog open
    // and show a validation message so the user can adjust and retry.
    const duplicateMessage =
      "A record with the same IP Address, User, and IP Color already exists.";
    if (isEditMode) {
      if (isEqual(payload, originalForm)) {
        showToaster(EDITRESOURCEMESSAGES.NO_UPDATE, SEVERITIES.INFO);
        return;
      }
      // Add/Update are audited server-side in Portal_AuditLog (via the _U/_I stored procedures).
      const result = await executeApi<IpWhiteListDto, number>(updateIpWhiteList, payload);
      if (result === -1) {
        setDuplicateError(duplicateMessage);   // shown as an in-dialog Alert; keep dialog open to let the user edit
        return;
      }
      if (result !== null) {
        showToaster("IP whitelist entry updated successfully.", SEVERITIES.SUCCESS);
        setIsFormOpen(false);
        await loadData();
      }
    } else {
      const result = await executeApi<IpWhiteListDto, number>(addIpWhiteList, payload);
      if (result === -1) {
        setDuplicateError(duplicateMessage);   // shown as an in-dialog Alert; keep dialog open to let the user edit
        return;
      }
      if (result !== null) {
        showToaster("IP whitelist entry added successfully.", SEVERITIES.SUCCESS);
        setIsFormOpen(false);
        await loadData();
      }
    }
  };

  const handleDelete = async (confirmed: boolean) => {
    if (confirmed && entryToDelete) {
      const result = await executeApi<number, number>(deleteIpWhiteList, entryToDelete.whitelistID);
      if (result !== null) {
        showToaster("IP whitelist entry deleted successfully.", SEVERITIES.SUCCESS);
        traceActivity(
          "Deleted item in Whitelisted IP List",
          entryToDelete.whitelistID,
          entryToDelete.ipAddress,
        );
        await loadData();
      }
    }
    setIsDeleteOpen(false);
    setEntryToDelete(null);
  };

  const columns: GridColDef[] = [
    { field: "ipAddress", headerName: "IP Address", minWidth: 150, flex: 1 },
    {
      field: "isHamaspikNetwork",
      headerName: "Hamaspik Network",
      minWidth: 140,
      flex: 0.8,
      valueFormatter: (value) => (value ? "Yes" : "No"),
    },
    { field: "userName", headerName: "User", minWidth: 150, flex: 1 },
    {
      field: "validFrom",
      headerName: "Valid From",
      minWidth: 120,
      flex: 0.8,
      valueFormatter: (value) =>
        value ? format(new Date(value), GLOBALSETTINGS.DATE_FORMAT) : "",
    },
    {
      field: "validUntil",
      headerName: "Valid Until",
      minWidth: 120,
      flex: 0.8,
      valueFormatter: (value) =>
        value ? format(new Date(value), GLOBALSETTINGS.DATE_FORMAT) : "",
    },
    { field: "memo", headerName: "Memo", minWidth: 200, flex: 1.5 },
    { field: "blacklistTypeName", headerName: "Blacklist Type", minWidth: 120, flex: 0.7 },
    {
      field: "ipColor",
      headerName: "IP Color",
      minWidth: 130,
      flex: 0.6,
      valueFormatter: (value) =>
        IP_COLOR_OPTIONS.find((o) => o.value === value)?.label ?? value ?? "",
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.1,
      minWidth: 100,
      hideable: false,
      filterable: false,
      sortable: false,
      disableColumnMenu: false,
      renderCell: (params) => (
        <Box display="flex" gap={0.5}>
          <GridActionsCellItem
            disabled={!canEdit}
            icon={
              <Tooltip title="Edit" placement="top">
                <EditIcon fontSize="small" />
              </Tooltip>
            }
            label="Edit"
            onClick={() => openEditDialog(params.row)}
          />
          <GridActionsCellItem
            disabled={!canDelete}
            icon={
              <Tooltip title="Delete" placement="top">
                <DeleteIcon fontSize="small" />
              </Tooltip>
            }
            label="Delete"
            onClick={() => openDeleteDialog(params.row)}
          />
        </Box>
      ),
    },
  ];

  const { columnState, pinnedColumns, setPinnedColumns, handleColumnResize } =
    useResizableColumns(columns);
  const { columnVisibilityModel, setColumnVisibilityModel } = usePersistedColumnVisibility(
    GRIDCOLUMNKEYS.IP_WHITELIST
  );

  return (
    <section className="mb-3">
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h5" component="div" className="mb-3 card-title">
            IP Whitelist
          </Typography>
          <Box component="form">
            <Grid2 container spacing={2}>
              <Grid2
                size={12}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "nowrap",
                }}
              >
                <SearchBox
                  searchPlaceholder={SEARCHBOX.TEXT_PLACEHOLDER}
                  onSearch={handleSearch}
                />
                {canEdit && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openAddDialog}
                    sx={{ ml: "auto" }}
                  >
                    Add IP
                  </Button>
                )}
              </Grid2>
              <Grid2 size={12}>
                <div style={{ height: 500, width: "100%" }}>
                  <DataGridPro
                    rows={filteredData}
                    columns={columnState}
                    getRowId={(row) => row.whitelistID}
                    disableMultipleRowSelection
                    rowHeight={dataGridRowHeight}
                    columnHeaderHeight={dataGridHeaderHeight}
                    loading={loading}
                    slots={{ loadingOverlay: CustomLoadingOverlay }}
                    hideFooter
                    pinnedColumns={pinnedColumns}
                    onPinnedColumnsChange={(newPinned) => setPinnedColumns(newPinned)}
                    onColumnWidthChange={(params) =>
                      handleColumnResize(params.width, params.colDef)
                    }
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={setColumnVisibilityModel}
                    localeText={{ noRowsLabel: "No IP whitelist entries found." }}
                  />
                </div>
              </Grid2>
            </Grid2>
          </Box>
        </CardContent>
      </Card>

      {/* Add / Edit Dialog */}
      <Dialog open={isFormOpen} onClose={() => setIsFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditMode ? "Edit IP Whitelist Entry" : "Add IP Whitelist Entry"}</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => setIsFormOpen(false)}
          className="dialog-close-button"
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          {duplicateError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {duplicateError}
            </Alert>
          )}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid2 container spacing={2} sx={{ mt: 0.5 }}>
            <Grid2 size={12}>
              <TextField
                label="IP Address"
                value={form.ipAddress}
                onChange={(e) => handleFormChange("ipAddress", e.target.value)}
                fullWidth
                required
                size="small"
                error={!!ipAddressError}
                helperText={ipAddressError}
                inputProps={{ maxLength: 15 }}
              />
            </Grid2>
            <Grid2 size={12}>
              <Autocomplete
                options={users}
                getOptionLabel={userDisplay}
                filterOptions={(opts) => opts}
                value={selectedUser}
                inputValue={userInput}
                loading={usersLoading}
                noOptionsText={
                  usersLoading
                    ? "Searching..."
                    : userInput.trim()
                      ? "No users found"
                      : "Type to search users"
                }
                onInputChange={(_, value, reason) => {
                  if (reason === "input" || reason === "clear") setUserInput(value);
                }}
                onChange={(_, selected) => {
                  setSelectedUser(selected);
                  setUserInput(selected ? userDisplay(selected) : "");
                  handleFormChange("userId", selected?.userId ?? null);
                }}
                isOptionEqualToValue={(option, selected) => option.userId === selected.userId}
                renderInput={(params) => (
                  <TextField {...params} label="User" placeholder="Please select" size="small" />
                )}
                size="small"
                fullWidth
              />
            </Grid2>
            <Grid2 size={6}>
              <DatePicker
                label="Valid From"
                value={form.validFrom ? parseLocalDate(form.validFrom.substring(0, 10)) : null}
                onChange={(newValue) =>
                  handleFormChange(
                    "validFrom",
                    newValue ? newValue.toLocaleDateString("en-CA") : null,
                  )
                }
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </Grid2>
            <Grid2 size={6}>
              <DatePicker
                label="Valid Until"
                value={form.validUntil ? parseLocalDate(form.validUntil.substring(0, 10)) : null}
                onChange={(newValue) =>
                  handleFormChange(
                    "validUntil",
                    newValue ? newValue.toLocaleDateString("en-CA") : null,
                  )
                }
                minDate={
                  form.validFrom && form.validFrom.substring(0, 10) > todayStr()
                    ? parseLocalDate(form.validFrom.substring(0, 10))
                    : parseLocalDate(todayStr())
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    error: !!validUntilError,
                    helperText: validUntilError,
                  },
                }}
              />
            </Grid2>
            <Grid2 size={12}>
              <TextField
                label="Memo"
                value={form.memo ?? ""}
                onChange={(e) => handleFormChange("memo", e.target.value || null)}
                fullWidth
                size="small"
                multiline
                rows={2}
                inputProps={{ maxLength: 200 }}
              />
            </Grid2>
            <Grid2 size={6}>
              <TextField
                select
                label="Blacklist Type"
                value={form.blacklistType ?? ""}
                onChange={(e) =>
                  handleFormChange(
                    "blacklistType",
                    e.target.value !== "" ? Number(e.target.value) : null,
                  )
                }
                fullWidth
                size="small"
              >
                {blacklistTypes.map((t) => (
                  <MenuItem key={t.listId} value={t.listId}>
                    {t.listDescription}
                  </MenuItem>
                ))}
              </TextField>
            </Grid2>
            <Grid2 size={6}>
              <TextField
                select
                label="IP Color"
                value={form.ipColor ?? ""}
                onChange={(e) => handleFormChange("ipColor", e.target.value || null)}
                fullWidth
                size="small"
              >
                {IP_COLOR_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid2>
            <Grid2 size={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isHamaspikNetwork}
                    onChange={(e) =>
                      handleFormChange("isHamaspikNetwork", e.target.checked)
                    }
                  />
                }
                label="Hamaspik Network"
              />
            </Grid2>
          </Grid2>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="light" onClick={() => setIsFormOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={loading}>
            {isEditMode ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmationDialog
        open={isDeleteOpen}
        title="Delete IP Whitelist Entry"
        message={`Are you sure you want to delete the IP address "${entryToDelete?.ipAddress ?? ""}"?`}
        onClose={handleDelete}
      />
    </section>
  );
}

export default WhiteListedIpList;
